import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import useAuth from "@/hooks/useAuth";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { groupPortfoliosByType } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import {
  validateKeywordsSize,
  validateTermsTotal,
  MAX_KEYWORDS_COUNT,
  MAX_KEYWORDS_TOTAL_CHARS,
} from "@/pages/PortfolioBuilder/keywordLimits";
import type { Portfolio } from "@/types/Portfolio";
import { emptyNotifications, type FilterNotifications } from "@/types/notifications";
import toast from "react-hot-toast";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import * as Sentry from "@sentry/react";
import { derivedViewColor, nextViewColor } from "@/shared/utils/savedViewHighlight";
import {
  clearPrebuiltCombinedColor,
  getPrebuiltCombinedColorsSnapshot,
  setPrebuiltCombinedColor,
  subscribeToPrebuiltCombinedColors,
} from "@/pages/PortfolioBuilder/prebuiltCombinedOverrides";

import { toCategories, type Category } from "./tagCategories";
import { useTypedTerms } from "./useTypedTerms";
import {
  isIncludedTagWatchlist,
  storageFor,
  typedTermsWatchlist,
  type ListStorage,
} from "@/pages/PortfolioBuilder/listStorage";
import { formatListName } from "@/shared/utils/formatListName";
import useTabStore from "@/stores/useTabStore";
import {
  clearPrebuiltOverride,
  getPrebuiltOverridesSnapshot,
  setPrebuiltOverride,
  subscribeToPrebuiltOverrides,
} from "@/pages/PortfolioBuilder/prebuiltWatchlists";
import { useDemoGuideStore } from "@/demo-guide/useDemoGuideStore";
import { trackFeatureUsage } from "@/utils/gtm";

/**
 * Pre-fill for CREATE mode (concierge "describe it" flow) — seeds the form
 * without entering edit mode; saving still creates a new filter.
 *
 * A concierge filter is a UNION across axes (tags OR watchlists), so the
 * watchlist ids matter as much as the tags — carrying only `tags` silently
 * halves the filter's reach.
 */
/** Same members, order aside. */
const sameMembers = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

export interface FilterDraft {
  name?: string;
  searchKeyword?: string;
  tags?: string[];
  /** Ticker/keyword watchlists this filter matches against. */
  portfolioIds?: number[];
  /** Tag watchlists this filter matches against. */
  tagsWatchlistIds?: number[];
  filterMode?: "AND" | "OR";
  keywordOp?: "AND" | "OR";
  /** Typed keyword vs keyword-watchlist terms. Needed for an exact copy of a
   *  library filter; everything else leaves it at the default. */
  keywordWatchlistOp?: "AND" | "OR";
  /** Name of the Newsquawk combination this was opened from in the library —
   *  the builder then says so rather than "New". */
  basedOn?: string;
  /** That combination itself, so its colour can be kept for this user
   *  without copying it. See libraryChanges. */
  libraryId?: number;
  /** The colour the user currently sees it highlight in. */
  color?: string;
  /**
   * Tickers, companies and topics to start the typing field with — the
   * concierge's single watchlist, a create link, a list typed elsewhere.
   */
  tickers?: string[];
  keywords?: string[];
  /**
   * What the Newsquawk list being copied is. A watchlist's colour is kept as
   * the user's override in one store, a combination's in another.
   */
  libraryKind?: "watchlist" | "combined";
}

/** Which kind of record the builder is editing, if any. */
type EditingKind = ListStorage | "tag";

/**
 * A new list, reported as whatever it saved as. The onboarding rail's "Build
 * a watchlist" step and the checklist's create_watchlist task both complete
 * on this event, so every path that makes a list has to send it — edits and
 * conversions don't.
 */
function trackListCreated(detail: ListStorage | "demo") {
  trackFeatureUsage({
    feature_name: "watchlist",
    feature_action: "create",
    feature_detail: detail,
  });
}

export interface UseFilterBuilderOptions {
  queryId?: number | null;
  /**
   * A plain watchlist to edit here. It stays a watchlist while it is only
   * typed terms; given a tag, a list or a keyword expression, it becomes the
   * hidden half of a combined watchlist — see listStorage.ts.
   */
  portfolioId?: number | null;
  /**
   * A tag watchlist to edit. Customers no longer build these — tags are part
   * of any list — so saving one makes it a combined watchlist in its place.
   * One the desk is editing, or one another list includes, is saved in place
   * (see inPlaceReason).
   */
  tagWatchlistId?: number | null;
  draft?: FilterDraft | null;
  /**
   * Lists to build against instead of the signed-in user's own.
   *
   * Only the guided demo passes this. It opens this editor on a recorded plan
   * for a logged-out visitor, whose usePortfoliosCRUD is empty — so the lists
   * the draft selects would have no rows to sit in and the left pane would
   * come up blank next to a query referring to them. Same override PlanPreview
   * takes, and for the same reason.
   */
  portfoliosOverride?: Portfolio[];
  /**
   * `id` is absent only for the guided demo, which writes nothing. `kind` is
   * which record the list ended up as; absent means combined.
   */
  onSaved?: (
    queryName: string,
    categories: string[],
    id?: number,
    kind?: EditingKind
  ) => void;
  onDeleted?: () => void;
}

export function useFilterBuilder(options: UseFilterBuilderOptions) {
  const confirm = useConfirm();
  const { authenticated } = useAuth();
  const { queriesQuery, createMutation, editMutation, deleteMutation } = useHeadlineQueriesCRUD();
  const {
    portfolios: ownPortfolios,
    createMutation: portfolioCreate,
    updateMutation: portfolioUpdate,
    deleteMutation: portfolioDelete,
  } = usePortfoliosCRUD();
  // The hook still runs unconditionally; the override just wins. Logged out it
  // resolves to an empty list anyway, so this costs nothing in the demo case.
  const portfolios = options.portfoliosOverride ?? ownPortfolios;
  const {
    watchlistsQuery: tagsWatchlistsQuery,
    updateMutation: tagWatchlistUpdate,
    deleteMutation: tagWatchlistDelete,
  } = useTagsWatchlistsCRUD();
  const markWatchlistCreated = useDemoGuideStore((s) => s.markWatchlistCreated);
  const tagsWatchlists = tagsWatchlistsQuery.data || [];
  const headlineQueries = queriesQuery.data;

  // ── Form state ────────────────────────────────────────────────────────
  const [queryName, setQueryName] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedTagsWatchlistIds, setSelectedTagsWatchlistIds] = useState<number[]>([]);
  const [filterMode, setFilterMode] = useState<"AND" | "OR">("OR");
  const [keywordOp, setKeywordOp] = useState<"AND" | "OR">("OR");
  const [keywordWatchlistOp, setKeywordWatchlistOp] = useState<"AND" | "OR">("OR");
  const [notifications, setNotifications] = useState<FilterNotifications>(emptyNotifications());
  /**
   * Highlight colour the user picked. Null means "not picked" — see
   * displayColor for what stands in, and what gets saved, until they do.
   */
  const [color, setColor] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /** What was typed into the list, with what each term matched. */
  const terms = useTypedTerms();
  /**
   * The hidden watchlist a combined list being edited keeps its typed terms
   * in. Its id comes out of the linked lists shown on the left, since the
   * typing field is where those terms are edited.
   */
  const [hiddenWatchlistId, setHiddenWatchlistId] = useState<number | null>(null);
  /**
   * Which record has been loaded into the form. The load effects depend on
   * the queries and lists, which refetch — on window focus among other
   * times — and reloading then threw away an edit in progress.
   */
  const loadedKey = useRef<string | null>(null);

  /**
   * Desk fields, for when this builder is the whole form: the full-screen
   * builder, which is also where a desk user's pencil on a Newsquawk
   * combination opens. Inert for everyone else — the UI is gated on
   * `useIsDeskUser()`.
   *
   * Seeded from the record being edited. The notes ride along on every save, so an edit that never loaded them sent
   * them back empty and cleared the desk's description and when-to-use.
   *
   * `publishToLibrary` is create-only: the server ignores owner_type on PATCH.
   */
  const [publishToLibrary, setPublishToLibrary] = useState(false);
  const [description, setDescription] = useState("");
  const [whenToUse, setWhenToUse] = useState("");
  const [assetClasses, setAssetClasses] = useState<string[]>([]);

  /** Which kind of record is being edited, if any. */
  const editingKind: EditingKind | undefined =
    options.queryId != null
      ? "combined"
      : options.portfolioId != null
        ? "watchlist"
        : options.tagWatchlistId != null
          ? "tag"
          : undefined;
  const isEditing = editingKind !== undefined;
  const editingPortfolio =
    options.portfolioId != null
      ? ownPortfolios.find((p) => p.id === options.portfolioId)
      : undefined;
  const editingTagWatchlist =
    options.tagWatchlistId != null
      ? tagsWatchlists.find((t) => t.id === options.tagWatchlistId)
      : undefined;
  const isNewsquawkOwned =
    editingKind === "combined"
      ? headlineQueries?.find((q) => q.id === options.queryId)?.owner_type ===
        "NEWSQUAWK"
      : editingKind === "tag"
        ? editingTagWatchlist?.owner_type === "NEWSQUAWK"
        : editingPortfolio?.owner_type === "NEWSQUAWK";

  const includedElsewhere =
    options.tagWatchlistId != null &&
    isIncludedTagWatchlist(headlineQueries, options.tagWatchlistId);

  /**
   * A list that saves in place and never converts: its own terms (or tags)
   * change; tags and other lists can't be added.
   *
   * - "library": the desk editing a Newsquawk watchlist or tag watchlist.
   *   Customers select library lists in their own dashboards by id. Turning
   *   one into a combined watchlist would leave every one of those selections
   *   pointing at a list they can no longer see — and the browser can only
   *   move the selections of whoever is signed in.
   * - "ingredient": a tag watchlist another list includes — see
   *   isIncludedTagWatchlist. In place, a change to its tags reaches every
   *   list that includes it.
   */
  const inPlaceReason: "library" | "ingredient" | undefined =
    (editingKind === "watchlist" || editingKind === "tag") && isNewsquawkOwned
      ? "library"
      : includedElsewhere
        ? "ingredient"
        : undefined;
  const inPlace = inPlaceReason !== undefined;

  // ── Portfolio grouping ────────────────────────────────────────────────
  const selectedGrouped = useMemo(() => {
    const selected = portfolios.filter((p) => selectedPortfolioIds.includes(p.id));
    return groupPortfoliosByType(selected);
  }, [portfolios, selectedPortfolioIds]);

  const hasBothWatchlistTypes =
    selectedGrouped.tickerOnly.length > 0 && selectedGrouped.keywordOnly.length > 0;

  // Only while a new combination is being put together. On an edit or a
  // library copy this fired as the stored lists loaded and silently turned a
  // saved OR into AND — a different list, saved without anyone asking.
  useEffect(() => {
    if (isEditing || options.draft) return;
    if (hasBothWatchlistTypes && keywordOp === "OR") {
      setKeywordOp("AND");
    }
  }, [hasBothWatchlistTypes]);

  // Resolve selected tag watchlist objects
  const selectedTagWatchlistObjects = useMemo(
    () => tagsWatchlists.filter((tw) => selectedTagsWatchlistIds.includes(tw.id)),
    [tagsWatchlists, selectedTagsWatchlistIds]
  );

  // ── Keyword validation ────────────────────────────────────────────────
  const aggregatedKeywords = useMemo(() => {
    const fromWatchlists = selectedGrouped.keywordOnly.flatMap((p) => p.keywords ?? []);
    const expression = searchKeyword.trim();
    return [
      ...terms.keywords,
      ...fromWatchlists,
      ...(expression ? [expression] : []),
    ];
  }, [selectedGrouped.keywordOnly, searchKeyword, terms.keywords]);

  const keywordsSize = validateKeywordsSize(aggregatedKeywords);

  const hasKeywords = searchKeyword.trim().length > 0 || selectedGrouped.keywordOnly.length > 0;
  /** Lists holding both tickers and keywords — OR'd with everything. */
  const hasMixedWatchlists = selectedGrouped.mixed.length > 0;
  const hasTickerWatchlists = selectedGrouped.tickerOnly.length > 0;
  const hasTags = selectedCategories.length > 0 || selectedTagsWatchlistIds.length > 0;
  const hasWatchlistsOrTags = hasTickerWatchlists || hasTags;

  // ── Counts per type ───────────────────────────────────────────────────
  const tickerCount = selectedGrouped.tickerOnly.length;
  const keywordCount = selectedGrouped.keywordOnly.length + (searchKeyword.trim() ? 1 : 0);
  const tagCount = selectedCategories.length + selectedTagsWatchlistIds.length;

  // ── Load existing query for editing ───────────────────────────────────
  useEffect(() => {
    if (editingKind !== "combined" || !headlineQueries) return;
    const key = `q-${options.queryId}`;
    if (loadedKey.current === key) return;
    const q = headlineQueries.find((query) => query.id === options.queryId);
    if (!q) return;
    loadedKey.current = key;

    // Typed terms back into the typing field, out of the linked lists.
    const hidden = typedTermsWatchlist(q);
    setHiddenWatchlistId(hidden?.id ?? null);
    terms.load({ tickers: hidden?.tickers, keywords: hidden?.keywords });

    setQueryName(q.queryName);
    setSearchKeyword(q.searchKeyword ?? "");
    setSelectedPortfolioIds(
      (q.portfolioIds ?? []).filter((id) => id !== hidden?.id)
    );
    setSelectedTagsWatchlistIds(q.tagsWatchlistIds ?? []);
    setFilterMode(q.filterMode ?? "OR");
    setKeywordOp(q.keywordOp ?? "OR");
    setKeywordWatchlistOp(q.keywordWatchlistOp ?? "OR");
    setColor(q.color ?? null);
    // Keep any extra backend sources from the API — PATCH replaces the whole map
    setNotifications(q.notifications ?? emptyNotifications());

    // Convert category IDs back to Category objects
    setSelectedCategories(toCategories(q.categories ?? []));

    setPublishToLibrary(q.owner_type === "NEWSQUAWK");
    setDescription(q.description ?? "");
    setWhenToUse(q.whenToUse ?? "");
    setAssetClasses(q.assetClasses ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.load is stable
  }, [editingKind, options.queryId, headlineQueries]);

  // ── Load a plain watchlist for editing ────────────────────────────────
  useEffect(() => {
    if (editingKind !== "watchlist" || !editingPortfolio) return;
    const key = `p-${editingPortfolio.id}`;
    if (loadedKey.current === key) return;
    loadedKey.current = key;

    const p = editingPortfolio;
    setQueryName(p.name);
    terms.load({ tickers: p.tickers, keywords: p.keywords });
    setHiddenWatchlistId(null);
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
    setColor(p.color ?? null);
    setNotifications(p.notifications ?? emptyNotifications());
    setPublishToLibrary(p.owner_type === "NEWSQUAWK");
    setDescription(p.description ?? "");
    setWhenToUse(p.when_to_use ?? "");
    setAssetClasses(p.categories ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.load is stable
  }, [editingKind, editingPortfolio]);

  // ── Load a tag watchlist for editing ──────────────────────────────────
  useEffect(() => {
    if (editingKind !== "tag" || !editingTagWatchlist) return;
    const key = `t-${editingTagWatchlist.id}`;
    if (loadedKey.current === key) return;
    loadedKey.current = key;

    const t = editingTagWatchlist;
    setQueryName(t.name);
    terms.clear();
    setHiddenWatchlistId(null);
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedTagsWatchlistIds([]);
    setSelectedCategories(toCategories(t.tags));
    setColor(null);
    setNotifications(t.notifications ?? emptyNotifications());
    setPublishToLibrary(t.owner_type === "NEWSQUAWK");
    setDescription(t.description ?? "");
    setWhenToUse(t.when_to_use ?? "");
    setAssetClasses(t.categories ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.clear is stable
  }, [editingKind, editingTagWatchlist]);

  // ── Seed from a concierge draft (create mode only) ────────────────────
  useEffect(() => {
    const draft = options.draft;
    if (!draft || isEditing) return;
    setQueryName(draft.name ?? "");
    setSearchKeyword(draft.searchKeyword ?? "");
    if (draft.filterMode) setFilterMode(draft.filterMode);
    if (draft.keywordOp) setKeywordOp(draft.keywordOp);
    if (draft.keywordWatchlistOp) setKeywordWatchlistOp(draft.keywordWatchlistOp);
    setSelectedCategories(toCategories(draft.tags ?? []));
    // The other half of the union — without these a "tags [Fed] OR
    // watchlists [Fed Speakers]" filter arrives as tags only.
    setSelectedPortfolioIds(draft.portfolioIds ?? []);
    setSelectedTagsWatchlistIds(draft.tagsWatchlistIds ?? []);
    setColor(draft.color ?? null);
    terms.load({ tickers: draft.tickers, keywords: draft.keywords });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.load is stable
  }, [options.draft, isEditing]);

  /**
   * What the picker shows until they pick.
   *
   * A new list gets the next palette colour, and is saved with it — it has no
   * id yet, so the id-derived colour it would otherwise fall back to cannot be
   * previewed. An existing list with no stored colour shows the derived one
   * it is already highlighting in, and keeps it unless changed.
   */
  const ownQueryCount =
    headlineQueries?.filter((q) => q.owner_type === "USER").length ?? 0;
  const displayColor =
    color ??
    (editingKind === "combined"
      ? derivedViewColor(options.queryId ?? undefined)
      : nextViewColor(ownQueryCount));
  const colorToSave = isEditing ? color : displayColor;

  // ── Toggle helpers ────────────────────────────────────────────────────
  const togglePortfolio = useCallback((id: number) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleCategory = useCallback((cat: Category) => {
    setSelectedCategories((prev) => {
      const exists = prev.some((c) => c.id === cat.id);
      return exists ? prev.filter((c) => c.id !== cat.id) : [...prev, cat];
    });
  }, []);

  const toggleTagWatchlist = useCallback((id: number) => {
    setSelectedTagsWatchlistIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const clearAll = useCallback(() => {
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
    terms.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.clear is stable
  }, []);

  const reset = useCallback(() => {
    setQueryName("");
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
    setNotifications(emptyNotifications());
    setColor(null);
    setPublishToLibrary(false);
    setDescription("");
    setWhenToUse("");
    setAssetClasses([]);
    // Operators too: they carried over into the next new list.
    setFilterMode("OR");
    setKeywordOp("OR");
    setKeywordWatchlistOp("OR");
    terms.clear();
    setHiddenWatchlistId(null);
    loadedKey.current = null;
    setFormErrors({ categories: "", keywords: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.clear is stable
  }, []);

  // ── Semantic summary ──────────────────────────────────────────────────
  const semanticSummary = useMemo(() => {
    const parts: string[] = [];

    // Keywords
    const kwParts: string[] = [];
    if (searchKeyword.trim()) kwParts.push(`"${searchKeyword.trim()}"`);
    selectedGrouped.keywordOnly.forEach((p) => kwParts.push(p.name));
    if (kwParts.length > 0) {
      const joiner = keywordWatchlistOp === "AND" ? " and " : " or ";
      parts.push(`containing ${kwParts.join(joiner)}`);
    }

    // Tickers
    if (selectedGrouped.tickerOnly.length > 0) {
      const names = selectedGrouped.tickerOnly.map((p) => p.name);
      parts.push(`mentioning ${names.join(" or ")} tickers`);
    }

    // Tags
    const tagParts: string[] = [];
    if (selectedCategories.length > 0) tagParts.push(...selectedCategories.map((c) => c.title));
    if (selectedTagWatchlistObjects.length > 0) tagParts.push(...selectedTagWatchlistObjects.map((tw) => tw.name));
    if (tagParts.length > 0) {
      parts.push(`tagged ${tagParts.join(" or ")}`);
    }

    if (parts.length === 0) return "No filters selected";
    const joiner = keywordOp === "AND" ? " AND " : " OR ";
    return "Headlines " + parts.join(joiner);
  }, [searchKeyword, selectedGrouped, selectedCategories, selectedTagWatchlistObjects, keywordOp, keywordWatchlistOp]);

  // ── Submit ────────────────────────────────────────────────────────────
  const [formErrors, setFormErrors] = useState({ categories: "", keywords: "" });

  /**
   * What has changed on a Newsquawk list opened from the library as a copy,
   * split by what saving it has to do. Its terms, lists, tags, name or alerts
   * need a list of the user's own; its colour alone can be kept as their
   * override of the library one. Lists have no priority: which one wins
   * belongs to each widget (highlightOrder.ts).
   */
  const libraryChanges = useMemo(() => {
    const draft = options.draft;
    if (isEditing || !draft?.basedOn) return null;
    const content = !(
      // A copied watchlist's terms arrive in the typing field, so they are
      // compared with what was copied; for anything else that is nothing.
      sameMembers(terms.tickers, draft.tickers ?? []) &&
      sameMembers(terms.keywords, draft.keywords ?? []) &&
      terms.keptTags.length === 0 &&
      queryName.trim() === (draft.name ?? "").trim() &&
      searchKeyword.trim() === (draft.searchKeyword ?? "").trim() &&
      sameMembers(
        selectedCategories.map((c) => String(c.id)),
        draft.tags ?? []
      ) &&
      sameMembers(
        selectedPortfolioIds.map(String),
        (draft.portfolioIds ?? []).map(String)
      ) &&
      sameMembers(
        selectedTagsWatchlistIds.map(String),
        (draft.tagsWatchlistIds ?? []).map(String)
      ) &&
      // Only where the copy set them; otherwise they sit at their defaults.
      (draft.filterMode === undefined || filterMode === draft.filterMode) &&
      (draft.keywordOp === undefined || keywordOp === draft.keywordOp) &&
      (draft.keywordWatchlistOp === undefined ||
        keywordWatchlistOp === draft.keywordWatchlistOp) &&
      JSON.stringify(notifications) === JSON.stringify(emptyNotifications())
    );
    const look = color !== (draft.color ?? null);
    return { content, look };
  }, [
    options.draft,
    isEditing,
    queryName,
    searchKeyword,
    selectedCategories,
    selectedPortfolioIds,
    selectedTagsWatchlistIds,
    filterMode,
    keywordOp,
    keywordWatchlistOp,
    color,
    notifications,
    terms.tickers,
    terms.keywords,
    terms.keptTags.length,
  ]);

  const libraryId = libraryChanges ? options.draft?.libraryId : undefined;
  const copyUnchanged =
    !!libraryChanges && !libraryChanges.content && !libraryChanges.look;
  const libraryKind = options.draft?.libraryKind ?? "combined";
  /** Only the look differs, on a library list it can be kept for. */
  const lookOnly =
    !!libraryChanges &&
    !libraryChanges.content &&
    libraryChanges.look &&
    libraryId != null;

  const combinedColors = useSyncExternalStore(
    subscribeToPrebuiltCombinedColors,
    getPrebuiltCombinedColorsSnapshot,
    getPrebuiltCombinedColorsSnapshot
  );
  const watchlistOverrides = useSyncExternalStore(
    subscribeToPrebuiltOverrides,
    getPrebuiltOverridesSnapshot,
    getPrebuiltOverridesSnapshot
  );
  const hasLibraryOverride =
    libraryId != null &&
    (libraryKind === "watchlist"
      ? watchlistOverrides[libraryId]?.color != null
      : combinedColors[libraryId] != null);

  /** Back to the look the desk set; closes the builder like a save. */
  const resetLibraryLook = useCallback(() => {
    if (libraryId == null) return;
    if (libraryKind === "watchlist") {
      // The colour only: an old priority stored beside it is left for the
      // one-time highlight-order conversion to read (useSeedHighlightOrders).
      clearPrebuiltOverride(libraryId, "color");
      toast.success(`“${options.draft?.basedOn}” is back to Newsquawk's colour`);
      options.onSaved?.(queryName, [], libraryId, "watchlist");
      return;
    }
    clearPrebuiltCombinedColor(libraryId);
    toast.success(`“${options.draft?.basedOn}” is back to Newsquawk's colour`);
    options.onSaved?.(queryName, [], libraryId);
  }, [libraryId, libraryKind, options, queryName]);

  // What the list would save as right now: which controls apply, and where
  // handleSubmit sends it. In place, it stays what it is.
  const parts = {
    hasTypedTerms: terms.hasTerms,
    keptTags: terms.keptTags.length,
    pickedTags: selectedCategories.length,
    linkedLists: selectedPortfolioIds.length,
    tagWatchlists: selectedTagsWatchlistIds.length,
    hasKeywordExpression: searchKeyword.trim() !== "",
  };
  const storage: EditingKind =
    inPlace && editingKind ? editingKind : storageFor(parts, editingKind);

  const moveListToCombined = useTabStore((state) => state.moveListToCombined);

  const handleSubmit = useCallback(async () => {
    if (copyUnchanged) return;
    // Only the look changed: keep it for this user; nothing is created.
    if (lookOnly && libraryId != null) {
      if (libraryKind === "watchlist") {
        setPrebuiltOverride(libraryId, { color: color ?? displayColor });
        toast.success(`Colour saved for “${options.draft?.basedOn}”`);
        options.onSaved?.(queryName, [], libraryId, "watchlist");
        return;
      }
      if (color) {
        setPrebuiltCombinedColor(libraryId, color);
        toast.success(`Colour saved for “${options.draft?.basedOn}”`);
        options.onSaved?.(queryName, [], libraryId);
        return;
      }
    }
    setFormErrors({ categories: "", keywords: "" });

    if (
      !parts.hasTypedTerms &&
      parts.pickedTags === 0 &&
      parts.linkedLists === 0 &&
      parts.tagWatchlists === 0 &&
      !parts.hasKeywordExpression
    ) {
      setFormErrors((prev) => ({
        ...prev,
        categories:
          "Add a ticker, company or topic, or pick watchlists or tags on the left.",
      }));
      return;
    }

    if (aggregatedKeywords.length > 0 && !keywordsSize.ok) {
      setFormErrors((prev) => ({
        ...prev,
        keywords: keywordsSize.error ?? "Keyword limit exceeded.",
      }));
      return;
    }
    const termsTotal = validateTermsTotal(terms.tickers, terms.keywords);
    if (!termsTotal.ok) {
      setFormErrors((prev) => ({
        ...prev,
        keywords: termsTotal.error ?? "Too many entries.",
      }));
      return;
    }

    const desk = {
      publishToLibrary,
      description,
      whenToUse,
      categories: assetClasses,
    };

    // Kept tags from the typing field and tags picked on the left: to the
    // combined watchlist either way, since a watchlist holds none.
    const categories = [
      ...new Set([
        ...selectedCategories.map((cat) => String(cat.id ?? "")),
        ...terms.keptTags,
      ]),
    ];
    // A half-typed hex from the text field is not a colour.
    const validColor =
      colorToSave && /^#[0-9a-fA-F]{6}$/.test(colorToSave) ? colorToSave : undefined;

    if (!authenticated) {
      /**
       * Guided demo: nothing is written.
       *
       * Saving a list needs an account — /portfolios and /headline-queries
       * both refuse without one — so the button that ends the demo's most
       * important step failed for exactly the audience it was built for.
       * Nothing to create and nothing to clean up either: the demo watchlist
       * is already in the panels via demoWatchlist.ts, so "saving" reports
       * the truth about what an account would do.
       */
      toast.success("Watchlist created");
      // The guided demo's watchlist step ends here when the concierge's list
      // was opened in the builder; the store ignores a second call.
      markWatchlistCreated();
      trackListCreated("demo");
      options.onSaved?.(queryName, categories, undefined, storage);
      return;
    }

    setIsSaving(true);
    // Undone if what follows fails: a hidden watchlist nothing links to, or a
    // watchlist half-converted and so hidden with no combined list to show it.
    let createdHiddenId: number | undefined;
    let convertedId: number | undefined;
    try {
      // ── A tag watchlist saved in place — see inPlaceReason ─────────────
      if (storage === "tag" && options.tagWatchlistId != null) {
        const tags = selectedCategories.map((cat) => String(cat.id ?? ""));
        await tagWatchlistUpdate.mutateAsync({
          id: options.tagWatchlistId,
          data: {
            name: formatListName(queryName),
            tags,
            notifications,
            description: desk.description.trim() || null,
            when_to_use: desk.whenToUse.trim() || null,
            categories: desk.categories,
          },
        });
        toast.success("Watchlist updated");
        options.onSaved?.(queryName, tags, options.tagWatchlistId, "tag");
        return;
      }

      // ── Typed terms alone: a plain watchlist ────────────────────────────
      if (storage === "watchlist") {
        const fields = {
          name: formatListName(queryName),
          tickers: terms.tickers,
          keywords: terms.keywords,
          color: validColor ?? displayColor,
          notifications,
          description: desk.description.trim() || null,
          when_to_use: desk.whenToUse.trim() || null,
          categories: desk.categories,
        };
        let savedId: number;
        if (editingKind === "watchlist" && options.portfolioId != null) {
          // Only what this form edits — never a priority, which would make
          // the server renumber every list at or below it.
          await portfolioUpdate.mutateAsync({
            id: options.portfolioId,
            ...fields,
          } as Portfolio);
          // The desk's own local colour would mask the colour just saved to
          // the library list.
          if (isNewsquawkOwned) clearPrebuiltOverride(options.portfolioId, "color");
          toast.success("Watchlist updated");
          savedId = options.portfolioId;
        } else {
          const created = await portfolioCreate.mutateAsync({
            id: 0,
            ...fields,
            customizations: { includeRelatedHeadlines: false },
            owner_type: desk.publishToLibrary ? "NEWSQUAWK" : "USER",
          } as Portfolio);
          toast.success("Watchlist created");
          trackListCreated("watchlist");
          savedId = created.id;
        }
        options.onSaved?.(queryName, [], savedId, "watchlist");
        return;
      }

      // ── Anything more: a combined watchlist ─────────────────────────────
      // Its typed terms live in a hidden watchlist inside it — see
      // listStorage.ts.
      let typedId: number | undefined;
      if (terms.hasTerms) {
        const termFields = {
          name: formatListName(queryName),
          tickers: terms.tickers,
          keywords: terms.keywords,
        };
        if (editingKind === "watchlist" && options.portfolioId != null) {
          // Converted in place: it keeps its id, and its dashboard selections
          // follow it to the combined watchlist below.
          await portfolioUpdate.mutateAsync({
            id: options.portfolioId,
            ...termFields,
            component_only: true,
          } as Portfolio);
          typedId = options.portfolioId;
          convertedId = options.portfolioId;
        } else if (hiddenWatchlistId != null) {
          await portfolioUpdate.mutateAsync({
            id: hiddenWatchlistId,
            ...termFields,
          } as Portfolio);
          typedId = hiddenWatchlistId;
        } else {
          const created = await portfolioCreate.mutateAsync({
            id: 0,
            ...termFields,
            component_only: true,
            color: validColor ?? displayColor,
            // The combined watchlist carries the alerts; this has no
            // subscription of its own to send them from.
            notifications: emptyNotifications(),
            customizations: { includeRelatedHeadlines: false },
            owner_type: desk.publishToLibrary ? "NEWSQUAWK" : "USER",
          } as Portfolio);
          typedId = created.id;
          createdHiddenId = created.id;

          /**
           * A pref-storage without customer ingredients (before PR #31) keeps
           * this as a list of its own. If only kept tags made this list
           * combined, save it as the watchlist it always would have been —
           * without them — rather than as a visible duplicate beside a
           * combined one.
           */
          if (!created.component_only && storageFor({ ...parts, keptTags: 0 }) === "watchlist") {
            toast.success("Watchlist created");
            trackListCreated("watchlist");
            options.onSaved?.(queryName, [], created.id, "watchlist");
            return;
          }
        }
      }

      /**
       * A list made of typed terms and their tags is a union by definition —
       * any of these, or anything tagged as them — so its operators are OR
       * whatever the toggles were left at. Only a list combining other lists
       * or a keyword expression has operators someone chose.
       */
      const onlyTyped =
        parts.linkedLists === 0 &&
        parts.tagWatchlists === 0 &&
        !parts.hasKeywordExpression;

      const payload = {
        queryName,
        categories,
        searchKeyword,
        portfolioIds: [
          ...(typedId != null ? [typedId] : []),
          ...selectedPortfolioIds,
        ],
        tagsWatchlistIds: selectedTagsWatchlistIds,
        filterMode: onlyTyped ? ("OR" as const) : filterMode,
        keywordOp: onlyTyped ? ("OR" as const) : keywordOp,
        keywordWatchlistOp: onlyTyped ? ("OR" as const) : keywordWatchlistOp,
        notifications,
        // Omitted when not picked on an edit, so the stored value is left alone.
        color: validColor,
        description: desk.description.trim() || null,
        whenToUse: desk.whenToUse.trim() || null,
        assetClasses: desk.categories,
        // Create-only; the server ignores owner_type on PATCH.
        ...(desk.publishToLibrary ? ({ owner_type: "NEWSQUAWK" } as const) : {}),
      };

      let savedId: number;
      if (editingKind === "combined") {
        await editMutation.mutateAsync({ id: options.queryId as number, ...payload });
        savedId = options.queryId as number;
        // The typed terms were all removed: the watchlist that held them goes
        // too, and its link with it.
        if (!terms.hasTerms && hiddenWatchlistId != null) {
          await portfolioDelete.mutateAsync(String(hiddenWatchlistId));
        }
        toast.success("Watchlist updated");
      } else {
        const response = await createMutation.mutateAsync({ id: undefined, ...payload });
        savedId = Number(response.data?.id);
        createdHiddenId = undefined;
        convertedId = undefined;
        if (editingKind === "watchlist" && options.portfolioId != null) {
          // Every widget that filtered or highlighted by the watchlist now
          // does so by the combined list it has become.
          moveListToCombined("watchlist", options.portfolioId, savedId);
          toast.success("Watchlist updated");
        } else if (editingKind === "tag" && options.tagWatchlistId != null) {
          // A tag watchlist is replaced by the combined watchlist holding its
          // tags: its dashboard selections move across, and it goes.
          moveListToCombined("tag", options.tagWatchlistId, savedId);
          await tagWatchlistDelete
            .mutateAsync(options.tagWatchlistId)
            .catch((error) => Sentry.captureException(error));
          toast.success("Watchlist updated");
        } else {
          toast.success("Watchlist created");
          trackListCreated("combined");
        }
      }
      options.onSaved?.(payload.queryName, payload.categories, savedId, "combined");
    } catch (error) {
      Sentry.captureException(error);
      // Best effort: nobody could see either to put it right by hand.
      if (createdHiddenId != null) {
        portfolioDelete.mutateAsync(String(createdHiddenId)).catch(() => {});
      }
      if (convertedId != null) {
        portfolioUpdate
          .mutateAsync({ id: convertedId, component_only: false } as Portfolio)
          .catch(() => {});
      }
      toast.error(isEditing ? "Failed to update watchlist" : "Failed to save watchlist");
    } finally {
      setIsSaving(false);
    }
  }, [
    queryName, selectedCategories, searchKeyword, selectedPortfolioIds,
    selectedTagsWatchlistIds, filterMode, keywordOp, keywordWatchlistOp,
    notifications, colorToSave, displayColor, editingKind, isEditing,
    options.queryId, options.portfolioId, aggregatedKeywords, keywordsSize,
    editMutation, createMutation, portfolioCreate, portfolioUpdate,
    portfolioDelete, options.onSaved,
    authenticated, copyUnchanged, lookOnly, libraryId, color, publishToLibrary,
    description, whenToUse, assetClasses, terms.hasTerms, terms.keptTags,
    terms.tickers, terms.keywords, hiddenWatchlistId, moveListToCombined,
    options.draft?.basedOn, options.tagWatchlistId, storage, parts, libraryKind,
    markWatchlistCreated, tagWatchlistUpdate, tagWatchlistDelete,
    isNewsquawkOwned,
  ]);

  const handleDelete = useCallback(async () => {
    const id =
      editingKind === "combined"
        ? options.queryId
        : editingKind === "tag"
          ? options.tagWatchlistId
          : options.portfolioId;
    if (id == null) return;
    const ok = await confirm({
      title: "Delete watchlist",
      message: "This watchlist will be permanently deleted. This action cannot be undone.",
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;

    setIsDeleting(true);
    try {
      if (editingKind === "combined") {
        await deleteMutation.mutateAsync(id);
        // pref-storage deletes the hidden watchlist with it (PR #31); this
        // covers a server from before that, and 404s harmlessly after.
        if (hiddenWatchlistId != null) {
          portfolioDelete.mutateAsync(String(hiddenWatchlistId)).catch(() => {});
        }
      } else if (editingKind === "tag") {
        await tagWatchlistDelete.mutateAsync(id);
      } else {
        await portfolioDelete.mutateAsync(String(id));
      }
      toast.success("Watchlist deleted");
      options.onDeleted?.();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to delete watchlist");
    } finally {
      setIsDeleting(false);
    }
  }, [
    editingKind,
    options.queryId,
    options.portfolioId,
    options.tagWatchlistId,
    deleteMutation,
    portfolioDelete,
    tagWatchlistDelete,
    hiddenWatchlistId,
    options.onDeleted,
    confirm,
  ]);

  return {
    // Form state
    queryName, setQueryName,
    /** The typing field: tickers, companies and topics, with their matches. */
    terms,
    searchKeyword, setSearchKeyword,
    selectedPortfolioIds, setSelectedPortfolioIds,
    selectedCategories, setSelectedCategories,
    selectedTagsWatchlistIds, setSelectedTagsWatchlistIds,
    filterMode, setFilterMode,
    keywordOp, setKeywordOp,
    keywordWatchlistOp, setKeywordWatchlistOp,
    notifications, setNotifications,
    color, setColor, displayColor,

    // Desk-only (see useIsDeskUser)
    publishToLibrary, setPublishToLibrary,
    description, setDescription,
    whenToUse, setWhenToUse,
    assetClasses, setAssetClasses,
    isNewsquawkOwned,

    // Toggles
    togglePortfolio,
    toggleCategory,
    toggleTagWatchlist,
    clearAll,
    reset,

    // Derived
    isEditing,
    /** Which record is being edited: a watchlist, tag watchlist or combined one. */
    editingKind,
    /** Saved in place, never converted — see inPlaceReason above. */
    inPlace,
    inPlaceReason,
    /** What the Newsquawk list being copied is, for the look override. */
    libraryKind,
    hasMixedWatchlists,
    /** The Newsquawk list being copied, if this is one. */
    basedOn: isEditing ? undefined : options.draft?.basedOn,
    /** Opened with a list the concierge assembled, rather than a copy. */
    fromDraft: !isEditing && !!options.draft && !options.draft.basedOn,
    copyUnchanged,
    /** The look can be kept for this user — see libraryChanges. */
    libraryLookable: libraryId != null,
    /** Terms, lists, name or alerts differ, so saving makes a new list. */
    libraryContentChanged: !!libraryChanges?.content,
    lookOnly,
    hasLibraryOverride,
    resetLibraryLook,
    selectedGrouped,
    selectedTagWatchlistObjects,
    semanticSummary,
    keywordsSize,
    hasKeywords,
    hasTickerWatchlists,
    hasTags,
    hasWatchlistsOrTags,
    tickerCount,
    keywordCount,
    tagCount,
    formErrors,

    // Data
    portfolios,
    tagsWatchlists,

    // Mutations
    handleSubmit,
    handleDelete,
    isSubmitting: isSaving || createMutation.isPending || editMutation.isPending,
    isDeleting,

    // Keyword limits
    MAX_KEYWORDS_COUNT,
    MAX_KEYWORDS_TOTAL_CHARS,
  };
}

export type UseFilterBuilderReturn = ReturnType<typeof useFilterBuilder>;
